import { MUICheckBox, MUISelect, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../services/api"
import { FieldRow, FormPage } from "../../../components/FormPage"
import {
  createFactoryValidationSchemas,
  useFactoryCheckboxHelper,
} from "../../../components/Helpers"
import { FactoryPost, FactoryTypeEnum } from "../../../models/Factory"
import { ControllerRendererProps } from "../../../types/reactHookForm"

const { FactoryTypeSchema, validationSchema } = createFactoryValidationSchemas()

const returnURL = `/factory-reports#factories`

export function FactoryCreate() {
  const navigate = useNavigate()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<FactoryPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      name: "",
      type: "" as keyof typeof FactoryTypeEnum,
      isFree: false,
      isEnabled: false,
      maxDays: 1,
    })
  }, [reset])

  const onSubmit = useCallback(
    async (values: FactoryPost) => {
      try {
        const res = await digitalManagerApi.post(`/factories`, {
          name: values.name,
          type: values.type,
          isFree: values.isFree,
          isEnabled: values.isEnabled,
          maxDays: values.maxDays,
        })
        if (res.status === StatusCodes.CREATED) {
          toast.success("Factory created")
          navigate(returnURL)
        }
      } catch {
        toast.error("Cannot create factory")
      }
    },
    [navigate]
  )

  const handleParsedNumberChange = useCallback(
    (field: ControllerRenderProps<FactoryPost, "maxDays">) => (value: string) => {
      const parsed = value ? Number.parseInt(value, 10) : ""
      field.onChange(parsed)
    },
    []
  )

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<FactoryPost, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: ControllerRendererProps<FactoryPost, "name">) => (
      <MUITextField
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextFieldChange]
  )

  const handleTypeSelectChange = useCallback(
    (field: ControllerRenderProps<FactoryPost, "type">) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : first?.id ?? ""
        field.onChange(value)
      },
    []
  )

  const renderTypeSelect = useCallback(
    ({ field }: ControllerRendererProps<FactoryPost, "type">) => (
      <MUISelect
        name={field.name}
        id="type"
        size={32}
        dropdownLOV={Object.entries(FactoryTypeEnum).map(([key, value]) => ({
          id: String(key),
          name: String(value),
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleTypeSelectChange(field)}
        onBlur={field.onBlur}
      />
    ),
    [handleTypeSelectChange]
  )

  const { handleBooleanToggle, renderCheckboxIsFree } =
    useFactoryCheckboxHelper<FactoryPost>({
      renderCheckbox: (field, handleToggle) => (
        <MUICheckBox
          id={field.name}
          checked={!!field.value}
          handleToggle={handleToggle}
        />
      ),
    })

  const renderCheckboxIsEnabled = useCallback(
    ({ field }: ControllerRendererProps<FactoryPost, "isEnabled">) => (
      <MUICheckBox
        id={field.name}
        checked={!!field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderMaxDaysTextField = useCallback(
    ({ field }: ControllerRendererProps<FactoryPost, "maxDays">) => (
      <MUITextField
        id="maxDays"
        type="number"
        size={32}
        name={field.name}
        value={
          field.value !== undefined && field.value !== null ? String(field.value) : ""
        }
        onChange={handleParsedNumberChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.maxDays?.message === "string"
            ? { maxDays: { message: errors.maxDays.message } }
            : undefined
        }
      />
    ),
    [errors.maxDays, handleParsedNumberChange]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderNameTextField} />
      </FieldRow>
      <FieldRow label="Type">
        <>
          <Controller control={control} name="type" render={renderTypeSelect} />
          {errors.type && (
            <MUITextField
              isDisabled
              value=""
              errors={{
                type: {
                  message:
                    typeof errors.type?.message === "string" ? errors.type.message : "",
                },
              }}
            />
          )}
        </>
      </FieldRow>
      <FieldRow label="Is Free">
        <Controller name="isFree" control={control} render={renderCheckboxIsFree} />
      </FieldRow>
      <FieldRow label="Is Enabled">
        <Controller name="isEnabled" control={control} render={renderCheckboxIsEnabled} />
      </FieldRow>
      <FieldRow label="Max Days">
        <Controller name="maxDays" control={control} render={renderMaxDaysTextField} />
      </FieldRow>
    </FormPage>
  )
}
